import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { uploadSchool, uploadSuper, fileUrl } from "@/lib/upload";
import { Field } from "@/components/Kit";
import { Save, ExternalLink, Plus, Trash2, ImagePlus, Loader2, X } from "lucide-react";

// Used by School Admin (no props) and by Super Admin (schoolId + schoolCode)
export default function WebsiteSettings({ schoolId, schoolCode }) {
  const qc = useQueryClient();
  const { school } = useAuth();
  const base = schoolId ? `/superadmin/schools/${schoolId}/website` : "/school/website";
  const upload = schoolId ? uploadSuper : uploadSchool;
  const code = schoolCode || school?.code;
  const { data } = useQuery({ queryKey: ["website", schoolId || "own"], queryFn: async () => (await api.get(base)).data });
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState("");
  useEffect(() => { if (data) setF({ ...data, facilities_text: data.facilities.join(", ") }); }, [data]);

  const save = useMutation({
    mutationFn: async () => {
      const body = { tagline: f.tagline, about: f.about, principal: f.principal, established: f.established, board: f.board, medium: f.medium, hero_image: f.hero_image, admission_open: f.admission_open, gallery: f.gallery,
        admission_intro: f.admission_intro || "", admission_notice: f.admission_notice || "",
        admission_required_docs: f.admission_required_docs || ["photo", "birth_cert"],
        facilities: f.facilities_text.split(",").map((s) => s.trim()).filter(Boolean),
        fee_structure: f.fee_structure.map((r) => ({ ...r, tuition: Number(r.tuition) || 0, books: Number(r.books) || 0, transport: Number(r.transport) || 0 })) };
      return (await api.put(base, body)).data;
    },
    onSuccess: () => { toast.success("Website content published"); qc.invalidateQueries({ queryKey: ["website"] }); qc.invalidateQueries({ queryKey: ["pub-schools"] }); qc.invalidateQueries({ queryKey: ["pub-school"] }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  if (!f) return null;
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const updFee = (i, k) => (e) => setF({ ...f, fee_structure: f.fee_structure.map((r, j) => (j === i ? { ...r, [k]: e.target.value } : r)) });
  const pick = (target) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(target);
    try {
      const r = await upload(file, target === "hero" ? "hero" : "gallery");
      setF((x) => (target === "hero" ? { ...x, hero_image: r.url } : { ...x, gallery: [...x.gallery, r.url] }));
      toast.success("Image uploaded — click Publish to save");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setBusy(""); }
  };

  return (
    <div className="grid gap-5 lg:grid-cols-5" data-testid="website-settings">
      <div className="ep-card p-5 lg:col-span-3 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold font-head text-white">Portal content</h3>
          <a href={`/s/${code}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-indigo-300 hover:text-white" data-testid="view-portal-link"><ExternalLink size={13} /> View public portal</a>
        </div>
        <Field label="Tagline" value={f.tagline} onChange={upd("tagline")} data-testid="ws-tagline" />
        <label className="block"><span className="mb-1.5 block text-xs font-medium text-slate-400">About the school</span><textarea className="ep-input" rows={4} value={f.about} onChange={upd("about")} data-testid="ws-about" /></label>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Principal" value={f.principal} onChange={upd("principal")} data-testid="ws-principal" />
          <Field label="Established" value={f.established} onChange={upd("established")} data-testid="ws-established" />
          <Field label="Board" value={f.board} onChange={upd("board")} data-testid="ws-board" />
          <Field label="Medium" value={f.medium} onChange={upd("medium")} data-testid="ws-medium" />
        </div>
        <Field label="Facilities (comma separated)" value={f.facilities_text} onChange={upd("facilities_text")} data-testid="ws-facilities" />

        <div>
          <span className="mb-1.5 block text-xs font-medium text-slate-400">Hero / cover photo</span>
          <div className="flex items-center gap-3">
            <div className="h-20 w-36 overflow-hidden rounded-xl bg-slate-800 ring-1 ring-slate-700">{f.hero_image && <img src={fileUrl(f.hero_image)} alt="" className="h-full w-full object-cover" />}</div>
            <label className="ep-btn ep-btn-ghost cursor-pointer !py-2 text-xs" data-testid="ws-hero-upload-label">{busy === "hero" ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />} Upload photo<input type="file" accept="image/*" className="hidden" onChange={pick("hero")} data-testid="ws-hero-upload" /></label>
            <input className="ep-input flex-1 !py-2 text-xs" placeholder="or paste image URL" value={f.hero_image} onChange={upd("hero_image")} data-testid="ws-hero" />
          </div>
        </div>
        <div>
          <span className="mb-1.5 block text-xs font-medium text-slate-400">Gallery photos ({f.gallery.length})</span>
          <div className="flex flex-wrap gap-2" data-testid="ws-gallery">
            {f.gallery.map((g, i) => (
              <div key={g + i} className="group relative h-16 w-16 overflow-hidden rounded-lg ring-1 ring-slate-700">
                <img src={fileUrl(g)} alt="" className="h-full w-full object-cover" />
                <button onClick={() => setF({ ...f, gallery: f.gallery.filter((_, j) => j !== i) })} className="absolute inset-0 flex items-center justify-center bg-black/60 text-white opacity-0 transition group-hover:opacity-100" data-testid={`ws-gallery-remove-${i}`}><X size={16} /></button>
              </div>
            ))}
            <label className="flex h-16 w-16 cursor-pointer items-center justify-center rounded-lg border border-dashed border-slate-600 text-slate-400 hover:border-indigo-500 hover:text-indigo-300" data-testid="ws-gallery-upload-label">{busy === "gallery" ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />}<input type="file" accept="image/*" className="hidden" onChange={pick("gallery")} data-testid="ws-gallery-upload" /></label>
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={!!f.admission_open} onChange={(e) => setF({ ...f, admission_open: e.target.checked })} className="h-4 w-4 accent-indigo-600" data-testid="ws-admission-open" /> Admissions open (shows Apply Now on portal)</label>

        <h3 className="pt-2 font-semibold font-head text-white">Online admission form</h3>
        <label className="block"><span className="mb-1.5 block text-xs font-medium text-slate-400">Intro text (shown above the form)</span>
          <textarea className="ep-input" rows={2} value={f.admission_intro || ""} onChange={upd("admission_intro")} placeholder="e.g. Fill this form to apply for the coming year. Keep the birth certificate and a passport photo ready." data-testid="ws-adm-intro" /></label>
        <Field label="Notice banner (optional)" value={f.admission_notice || ""} onChange={upd("admission_notice")} placeholder="e.g. Applications close on 30 June" data-testid="ws-adm-notice" />
        <div>
          <span className="mb-1.5 block text-xs font-medium text-slate-400">Documents required to apply</span>
          <div className="flex flex-wrap gap-3">
            {[["photo", "Student photo"], ["birth_cert", "Birth certificate"], ["tc", "Transfer certificate"], ["aadhaar_doc", "Aadhaar card"]].map(([k, lbl]) => {
              const on = (f.admission_required_docs || ["photo", "birth_cert"]).includes(k);
              return (
                <label key={k} className="flex items-center gap-1.5 text-sm text-slate-300">
                  <input type="checkbox" className="h-4 w-4 accent-indigo-600" checked={on} data-testid={`ws-adm-doc-${k}`}
                    onChange={(e) => {
                      const cur = new Set(f.admission_required_docs || ["photo", "birth_cert"]);
                      e.target.checked ? cur.add(k) : cur.delete(k);
                      setF({ ...f, admission_required_docs: [...cur] });
                    }} />
                  {lbl}
                </label>
              );
            })}
          </div>
        </div>
      </div>
      <div className="ep-card p-5 lg:col-span-2">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-semibold font-head text-white">Fee structure</h3>
          <button className="ep-btn ep-btn-ghost !px-2.5 !py-1.5 text-xs" onClick={() => setF({ ...f, fee_structure: [...f.fee_structure, { class_name: "", tuition: 0, books: 0, transport: 0 }] })} data-testid="ws-add-fee"><Plus size={13} /> Row</button>
        </div>
        <div className="space-y-2">
          {f.fee_structure.map((r, i) => (
            <div key={i} className="grid grid-cols-[1.4fr_1fr_1fr_1fr_auto] gap-1.5" data-testid={`ws-fee-row-${i}`}>
              <input className="ep-input !px-2 !py-1.5 text-xs" placeholder="Class" value={r.class_name} onChange={updFee(i, "class_name")} />
              <input className="ep-input !px-2 !py-1.5 text-xs" type="number" placeholder="Tuition" value={r.tuition} onChange={updFee(i, "tuition")} />
              <input className="ep-input !px-2 !py-1.5 text-xs" type="number" placeholder="Books" value={r.books} onChange={updFee(i, "books")} />
              <input className="ep-input !px-2 !py-1.5 text-xs" type="number" placeholder="Transport" value={r.transport} onChange={updFee(i, "transport")} />
              <button onClick={() => setF({ ...f, fee_structure: f.fee_structure.filter((_, j) => j !== i) })} className="text-slate-500 hover:text-rose-400"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
        <button className="ep-btn mt-5 w-full" disabled={save.isPending || !!busy} onClick={() => save.mutate()} data-testid="ws-save-btn"><Save size={15} /> {save.isPending ? "Publishing…" : "Publish to website"}</button>
      </div>
    </div>
  );
}
